import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from 'src/components/UI/Button';
import {
    useUserInfo,
    useRouter,
    useEventsReporter,
    eventMetricsMap,
    useGuestMode,
    useErTooltips,
    useStoreData,
} from 'src/hooks';
import { Body } from 'src/components/UI/Typography';
import Tooltip from 'src/components/Tooltip';
import styles from './styles.module.scss';

interface TabSectionProps {
    activeTabIndex: number;
    onToggle: (index: number) => void;
    distanceToTop: number;
    isHeaderCollapsed: boolean;
}

const TabsSection = ({
    activeTabIndex,
    onToggle,
    distanceToTop,
    isHeaderCollapsed,
}: TabSectionProps) => {
    const { userInfo } = useUserInfo();
    const { analyticsCustomEvent } = useEventsReporter();
    const leftTabRef = useRef<HTMLDivElement>(null);
    const rightTabRef = useRef<HTMLDivElement>(null);
    const leftButtonRef = useRef<HTMLButtonElement>(null);
    const rightButtonRef = useRef<HTMLButtonElement>(null);
    const {
        showTooltip,
        setTooltipStatusAsSeen,
        setShowTooltip,
        isTooltipSeen,
    } = useErTooltips();

    useEffect(() => {
        if (activeTabIndex === 1 && isTooltipSeen('storeCouponsTooltip')) {
            setShowTooltip('earnPointsTooltip');
        }

        if (activeTabIndex === 2) {
            setShowTooltip('storeCouponsTooltip');
        }

        return () => {
            if (showTooltip('earnPointsTooltip')) {
                setTooltipStatusAsSeen('earnPointsTooltip');
            }

            if (showTooltip('storeCouponsTooltip')) {
                setTooltipStatusAsSeen('storeCouponsTooltip');
            }
        };
    }, [
        activeTabIndex,
        isTooltipSeen('storeCouponsTooltip'),
        showTooltip('earnPointsTooltip'),
        showTooltip('storeCouponsTooltip'),
    ]);

    return (
        <>
            <div
                className={styles.headerWrapper}
                style={{
                    top: distanceToTop,
                    position: isHeaderCollapsed ? 'sticky' : 'static',
                }}
            >
                {/* switch button start */}
                <div className={styles.wrapper}>
                    <motion.div
                        initial={false}
                        animate={{
                            left:
                                activeTabIndex === 1
                                    ? 0
                                    : leftTabRef?.current?.offsetWidth,
                            width:
                                activeTabIndex === 1
                                    ? leftTabRef?.current?.offsetWidth
                                    : rightTabRef?.current?.offsetWidth,
                        }}
                        transition={{ type: 'tween' }}
                        className={styles.slidingPill}
                    />
                    <div
                        ref={leftTabRef}
                        className={`${styles.leftBtnWrapper} ${
                            activeTabIndex === 1 && styles.leftActive
                        }`}
                    >
                        <Tooltip
                            toolTipPlacement="bottom"
                            tooltipText="Easy Rewards offers to earn points on purchases."
                            elementRef={leftButtonRef}
                            onClose={() =>
                                setTooltipStatusAsSeen('earnPointsTooltip')
                            }
                            tooltipImgSrc={require('../../../assets/InfoTooltipImages/EarnPointsTooltipImg.png')}
                            showTooltip={showTooltip('earnPointsTooltip')}
                        >
                            <Button
                                ref={leftButtonRef}
                                variant="text"
                                role="tab"
                                className={styles.leftActive}
                                onClick={() => {
                                    console.log('EARN');
                                    analyticsCustomEvent(
                                        eventMetricsMap.easy_rewards,
                                        {
                                            click_text: 'Earn points',
                                            element_location:
                                                'rewards_navigation',
                                            event_rewards_number:
                                                userInfo?.rewardsNumber?.padStart(
                                                    10,
                                                    '0'
                                                ) || '',
                                        }
                                    );
                                    onToggle(1);
                                }}
                            >
                                <Body
                                    size={16}
                                    textAlign="textCenter"
                                    weight={
                                        activeTabIndex === 1 ? 'bold' : 'normal'
                                    }
                                >
                                    <motion.span
                                        style={{
                                            transition: 'color 0.5s ease-in',
                                        }}
                                        initial={false}
                                        animate={{
                                            color:
                                                activeTabIndex === 1
                                                    ? 'white'
                                                    : 'black',
                                        }}
                                        tabIndex={0}
                                        aria-selected={activeTabIndex === 1}
                                        id="earn_point_id"
                                        aria-label="Earn points, View all Easy Rewards offers here tab"
                                        onKeyDown={(e) => {
                                            if (
                                                e.key === 'Enter' ||
                                                e.key === ' '
                                            ) {
                                                e.preventDefault();
                                                analyticsCustomEvent(
                                                    eventMetricsMap.easy_rewards,
                                                    {
                                                        click_text:
                                                            'Earn points',
                                                        element_location:
                                                            'rewards_navigation',
                                                        event_rewards_number:
                                                            userInfo?.rewardsNumber ||
                                                            '',
                                                    }
                                                );
                                                onToggle(1);
                                            }
                                        }}
                                    >
                                        Earn points
                                    </motion.span>
                                </Body>
                            </Button>
                        </Tooltip>
                    </div>
                    <div
                        ref={rightTabRef}
                        className={`${styles.rightBtnWrapper} ${
                            activeTabIndex === 2 && styles.rightActive
                        }`}
                    >
                        <Tooltip
                            toolTipPlacement="bottom"
                            tooltipText="Store coupons to help you save every day."
                            elementRef={rightButtonRef}
                            tooltipImgSrc={require('../../../assets/InfoTooltipImages/StoreCouponsTooltipImg.png')}
                            onClose={() =>
                                setTooltipStatusAsSeen('storeCouponsTooltip')
                            }
                            showTooltip={showTooltip('storeCouponsTooltip')}
                        >
                            <Button
                                ref={rightButtonRef}
                                variant="text"
                                role="tab"
                                className={styles.rightActive}
                                onClick={() => {
                                    analyticsCustomEvent(
                                        eventMetricsMap.easy_rewards,
                                        {
                                            click_text: 'Store coupons',
                                            element_location:
                                                'rewards_navigation',
                                            event_rewards_number:
                                                userInfo?.rewardsNumber?.padStart(
                                                    10,
                                                    '0'
                                                ) || '',
                                        }
                                    );
                                    onToggle(2);
                                }}
                            >
                                <Body
                                    size={16}
                                    textAlign="textCenter"
                                    weight={
                                        activeTabIndex === 2 ? 'bold' : 'normal'
                                    }
                                >
                                    <motion.span
                                        style={{
                                            transition: 'color 0.5s ease-in',
                                        }}
                                        initial={false}
                                        animate={{
                                            color:
                                                activeTabIndex === 2
                                                    ? 'white'
                                                    : 'black',
                                        }}
                                        tabIndex={0}
                                        aria-selected={activeTabIndex === 2}
                                        id="store_coupons_id"
                                        aria-label="Store coupons, View savings on all your favorite products and services tab"
                                        onKeyDown={(e) => {
                                            if (
                                                e.key === 'Enter' ||
                                                e.key === ' '
                                            ) {
                                                e.preventDefault();
                                                analyticsCustomEvent(
                                                    eventMetricsMap.easy_rewards,
                                                    {
                                                        click_text:
                                                            'Store coupons',
                                                        element_location:
                                                            'rewards_navigation',
                                                        event_rewards_number:
                                                            userInfo?.rewardsNumber ||
                                                            '',
                                                    }
                                                );
                                                onToggle(2);
                                            }
                                        }}
                                    >
                                        Store coupons
                                    </motion.span>
                                </Body>
                            </Button>
                        </Tooltip>
                    </div>
                </div>
                {/* switch button end */}
            </div>
        </>
    );
};

export default TabsSection;
